import React, { useState, useEffect, useRef } from "react";
import { getPastXDays, getEndOfDay } from "../../util/DateUtility";
import { useCompany } from "../CompanySelection/CompanyContext";
import StatusFilter from '../../component/StatusFilter/StatusFilter';
import "./PaymentFilter.css"
import PaymentMethodFilter from "../PaymentMethodFilter/PaymentMethodFilter";
import { AmountFilter } from "../Filters/AmountFilter/AmountFilter";
import DateFilter from "../DateFilter/DateFilter";
import { DateFilterTypes } from '../../config/FilterOptionTypes';
import { MAX_DISBURSEMENT_QUERY_AMOUNT, MIN_DISBURSEMENT_QUERY_AMOUNT } from "../../util/EfupUtility";
import { SearchFilter } from "../SearchFilter/SearchFilter";
import { Link } from "react-router-dom";
import Modal from 'react-modal';

export function defaultFilter(lastXdays, source) {
    //lastXdays can be passed into the default filter to display a friendly date range label
    //lastXdays should be 365 from PaymentStatusTiles on dashboard
    const days = [30, 60, 90, 365]; //only allow friendly labels of Last 30/60/90 Days and Last 12 Months
    if (!days.includes(lastXdays)) {
        lastXdays = 30;
    }
    const yearRange = getPastXDays(lastXdays);

    const statusCodes =
    source === "PaymentSearch"
        ? ["open", "processing", "reconciled", "closed"]
        : ["open", "processing", "reconciled"];

    const filter = {
        sortColumn: "DATE",
        sortDirection: "desc",
        startDate: yearRange.startOn,
        endDate: yearRange.endOn,
        paymentMethods: ["CHK", "ACH", "ECHK", "P2D", "VC", ""],
        statusCodes,
        disbursementAmountMin: MIN_DISBURSEMENT_QUERY_AMOUNT,
        disbursementAmountMax: MAX_DISBURSEMENT_QUERY_AMOUNT,
        disbursementUuid: "",
        paymentNumber: "",
        partyName: "",
    };

    return filter;
};

export default function PaymentFilter(props) {
    const {companyNumber} = useCompany();
    const [options, setOptions] = useState(props.options);
    const amountFilterRef = useRef();
    const searchFilterRef = useRef()
    const [disableApply, setDisableApply] = useState(true);
    const [showCancel, setShowCancel] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [lastDateFilterType, setLastDateFilterType] = useState('');

    useEffect(() => {
        setup();
    }, []);     // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setOptions(props.options);
    }, [props.options]);     // eslint-disable-line react-hooks/exhaustive-deps

    const applyFilter = () => {
        if (props.onApplyFilter && options.startDate && options.endDate) {
            props.onApplyFilter(options);
        }

        checkShowCancel();
        setDisableApply(true);
        setIsModalOpen(false);
    };

    const cancelFilter = () => {
        setShowCancel(false);
        // setDisableApply(true);
        setDisableApply(false);

        // clear child components
        amountFilterRef.current.reset();
        searchFilterRef.current.reset();

        // clear parent component
        if (props.onCancelFilter) {
            props.onCancelFilter();
        }
    };

    // read values passed in and set defaults
    const setup = () => {
        const config = {
            companyNumber
        };

        // sortColumn
        if (!options.sortColumn) {
            config.sortColumn = "DATE";
        }

        // sort direction
        if (!options.sortDirection) {
            config.sortDirection = "DESC";
        }

        // date range
        //  If startOn/endOn are not provided but startDate/endDate are,
        //  convert startDate/endDate -> startOn/endOn.
        //  Otherwise we keep settings from dafaultFilter()
        if (!options.startDate) {
            config.startDate = getPastXDays(30).startOn;
        }

        if (!options.endDate) {
            config.endDate = getEndOfDay();
        }
        
        // methods
        if (!options.paymentMethods) {
            config.paymentMethods = ["CHK", "ACH", "ECHK", "P2D", "VC", ""];
        }

        // status codes
        if (!options.statusCodes) {
            config.statusCodes = ["open", "processing", "reconciled"];
        }

        // amounts
        if (!options.disbursementAmountMin) {
            config.disbursementAmountMin = MIN_DISBURSEMENT_QUERY_AMOUNT;
        }
        if (!options.disbursementAmountMax) {
            config.disbursementAmountMax = MAX_DISBURSEMENT_QUERY_AMOUNT;
        }

        setOptions(previousOptions => {
            return {
                ...previousOptions,
                ...config,
            }
        });

        checkShowCancel();

    }

    function handleDateChange(type, startDate, endDate) {
        setShowCancel(true);
        setDisableApply(false);

        let dateRange;
        switch (type) {
            case 'last30days':
                dateRange = getPastXDays(30);
                break;
            case 'last60days':
                dateRange = getPastXDays(60);
                break;
            case 'last90days':
                dateRange = getPastXDays(90);
                break;
            case 'last365days':
                dateRange = getPastXDays(365);
                break;
            case 'custom-date-range':
                if (lastDateFilterType === 'custom-date-range' && startDate && endDate) {
                    handleFilterChange('startDate', startDate);
                    handleFilterChange('endDate', endDate);
                } else {
                    handleFilterChange('startDate', null);
                    handleFilterChange('endDate', null);
                }
                
                break;
            default:
                // Revert back to default if no valid option was selected
                if (lastDateFilterType === 'custom-date-range' && startDate && endDate) {
                    handleFilterChange('startDate', startDate);
                    handleFilterChange('endDate', endDate);
                } else {
                    dateRange = getPastXDays(30);
                }
        }
        setLastDateFilterType(type);    
        if (type !== 'custom-date-range') {
            const startDate = dateRange?.startOn ?? "";
            const endDate = dateRange?.endOn ?? "";
            handleFilterChange('startDate', startDate);
            handleFilterChange('endDate', endDate);
        }
    };

    // update the status code values
    const handleStatusChange = (statusOptions) => {
        let optionsArray = statusOptions.map(option => { return option.value });
        if (!(optionsArray.includes('open') 
                && optionsArray.includes('processing') 
                && optionsArray.includes('reconciled') 
                && !optionsArray.includes('closed') 
                && !optionsArray.includes('all'))) {
            setShowCancel(true);
            setDisableApply(false);
        }

        // convert to format expected by search
        const codes = optionsArray.filter(option => { return option !== "all" });
        if(codes.join() != options.statusCodes.join()){
            setDisableApply(false);
        }

        // update payment filter state
        handleFilterChange("statusCodes", codes);
    };

    // update the payment method values
    const handlePaymentMethodChange = (options) => {
        if (options.filter(option => { return option.value === "all" }).length === 0 ) {
            setShowCancel(true);
            setDisableApply(false);
        }

        // convert to format expected by search
        const codes = options
            .filter(option => {return option.value !== "all"})
            .map(option => {return option.value});
        
        handleFilterChange("paymentMethods", codes);
    };

    // update the amount range selected by user
    const handleAmountChange = (startAmount, endAmount) => {
        if (startAmount !== MIN_DISBURSEMENT_QUERY_AMOUNT 
            && endAmount !== MAX_DISBURSEMENT_QUERY_AMOUNT) {
            setShowCancel(true);
            setDisableApply(false);
        }

        if (startAmount && !endAmount) {
            endAmount = startAmount;
        }
        handleFilterChange('disbursementAmountMin', startAmount)
        handleFilterChange('disbursementAmountMax', endAmount)
    }

    const handleSearchChange = (field, value) => {
        handleFilterChange(field, value);

        if (value !== "") {
            setShowCancel(true);
            setDisableApply(false);
        }
    }

    // update a single value
    const handleFilterChange = (field, value) => {
        setOptions(prev => {
            return {
                ...prev,
                [field]: value
            };
        });

    };

    const enableApply = () => {
        setDisableApply(false);
    }

    const checkShowCancel = () => {
        const yearRange = getPastXDays(30);
        
        if (options.sortColumn === "DATE" &&
            options.sortDirection === "desc" &&
            options.startDate.getTime() === yearRange.startOn.getTime() &&
            options.endDate.getTime() === yearRange.endOn.getTime() &&
            // TODO: how to check methods and codes, ...and work for all screens
            // options.paymentMethods == ["CHK", "ACH", "ECHK", "P2D", "VC", ""] &&
            (options.statusCodes.includes('open') && options.statusCodes.includes('processing') 
                && options.statusCodes.includes('reconciled') 
                && !options.statusCodes.includes('closed')) &&
            options.disbursementAmountMin === MIN_DISBURSEMENT_QUERY_AMOUNT &&
            options.disbursementAmountMax === MAX_DISBURSEMENT_QUERY_AMOUNT &&
            options.disbursementUuid === "" &&
            options.paymentNumber === "" &&
            options.partyName === "") {
            setShowCancel(false);
        } else {
            setShowCancel(true);
        }

    };

    const setParentDates = (start, end) => {
        handleFilterChange('startDate', start);
        handleFilterChange('endDate', end);
    }

    return (
        <React.Fragment>
            {/* <!-- Payment Lookup Search only visible on Mobile --> */}
            <div className="cb-row d-lg-none filter-payments-mobile">
                <div className="cb-col-xs-2">
                    <button className="btn" data-testid="mobile-filter-button" id="mobile-filter-button"><i className="fa fa-sliders fa-2x" onClick={() => setIsModalOpen(true)}></i></button>
                </div>
                <Modal
                    isOpen={isModalOpen}
                    overlayClassName="d-lg-none efup-mobile-filter-payments-overlay"
                    className="d-lg-none efup-mobile-filter-payments"
                    onRequestClose={() => setIsModalOpen(false)}
                ><div className="cb-modal-content mobile-filter-modal">
                        <h2 className="mobile-filters-header" data-testid="mobile-payment-filters-label">Filters</h2>
                        <i 
                            className="cb-close cb-icon icon-close" 
                            aria-hidden="true" 
                            data-dismiss="cb-modal" 
                            aria-label="Close" 
                            onClick={() => setIsModalOpen(false)}
                            data-testid="mobile-filter-payments-close-modal" />
                    <div className="mobile-filters-container">
                        <div className="mobile-filter">
                            <h5>Payment Method</h5>
                            <PaymentMethodFilter isMobile={true} onChange={handlePaymentMethodChange} options={props.paymentMethods} values={props.options.paymentMethods} />
                        </div>
                        <div className="mobile-filter">
                            <h5>Date</h5>
                            <DateFilter isMobile={true} onChange={handleDateChange} lastDateFilterType={lastDateFilterType} options={DateFilterTypes} defaultValue={DateFilterTypes[0]} setDisable={setDisableApply} setParentDates={setParentDates} startDate={options.startDate} endDate={options.endDate} filterLabel="Created Date:" />
                        </div>
                        <div className="mobile-filter">
                            <h5>Status</h5>
                            <StatusFilter isMobile={true} onChange={handleStatusChange} values={props.options.statusCodes} />
                        </div>
                        <div className="mobile-filter">
                            <h5>Amount</h5>
                            <AmountFilter isMobile={true} ref={amountFilterRef} onChange={handleAmountChange} min={props.options.disbursementAmountMin} max={props.options.disbursementAmountMax} />
                        </div>
                    </div>
                    <div className="mobile-filters-buttons">
                        {showCancel && 
                            <div className="filter-cancel" data-testid="filter-cancel" onClick={() => cancelFilter()}>Cancel Filter(s)</div>
                        }
                        <button 
                            className="cb-btn cb-btn-sm cb-btn-primary" 
                            data-testid="filter-apply" 
                            onClick={applyFilter}
                            disabled={disableApply}
                            >Apply Filter(s)
                        </button>
                    </div>
                </div></Modal>
            </div>

            <div className="cb-row d-none d-lg-block filter-payments">

                {/* <!-- Filter Headers--> */}
                <div className="cb-row d-none d-lg-block d-flex">
                    <div className="cb-col-lg-8">
                        <div className="sectionTitle" data-testid="payment-filter-filter-payments-label">Filter Payments</div>
                    </div>
                </div>

                <div className="cb-row d-none d-lg-block">

                    {/* dropdowns */}
                    <div className="cb-col-lg-12 d-flex filter-group">

                        <div className="d-flex">
                            <PaymentMethodFilter onChange={handlePaymentMethodChange} options={props.paymentMethods} values={props.options.paymentMethods} />
                            <DateFilter onChange={handleDateChange} options={DateFilterTypes} defaultValue={DateFilterTypes[0]} setDisable={setDisableApply} setParentDates={setParentDates} startDate={options.startDate} endDate={options.endDate} filterLabel="Created Date:" />
                            <StatusFilter onChange={handleStatusChange} values={props.options.statusCodes} />
                            <AmountFilter ref={amountFilterRef} onChange={handleAmountChange} min={props.options.disbursementAmountMin} max={props.options.disbursementAmountMax} />
                        </div>

                        {/* Buttons */}
                        <div className="button-group">
                            <button 
                                className="cb-btn cb-btn-sm cb-btn-primary" 
                                data-testid="filter-apply" 
                                onClick={applyFilter}
                                disabled={disableApply}
                            >Apply Filter(s)</button>
                            
                            { showCancel && 
                                <Link to="" data-testid="filter-cancel" onClick={() => cancelFilter()}>Cancel Filter(s)</Link>
                            }
                        </div>
                    </div>
                </div>

                <SearchFilter 
                    onChange={handleSearchChange}
                    enableApply={enableApply}
                    paymentNumber={options.paymentNumber}
                    partyName={options.partyName}
                    disbursementUuid={options.disbursementUuid}
                    vendorCode={options.vendorCode}
                    pageType={props.pageType}
                    ref={searchFilterRef}
                />

            </div>
        </React.Fragment>
    );
};
